import {getBaseURL, getPostRequestOptions} from '../utils';
import {AIAlertResponse} from './types/automationRuleTypes';
import {RuleType} from '../../../features/Automations/enums';
import {getToStatuses} from '../../../_shared/GWTBridge/invoke/bpm/bpmMain';

export const mockedRs: AIAlertResponse = {
    description: 'Allow status change for specific user when status is warned',
    condition: {
        logicOperator: 'AND',
        timingCondition: '',
        timingConditionConfig: null,
        rules: []
    },
    action: 'CONDITIONAL_TRANSITION',
    actionConfig: {
        assignToOrConditionalTransition: {
            entityType: 'USER',
            entityValues: ['igorl']
        }
    }
};

export const warnedRs: AIAlertResponse = {
    description: 'Send an email when cost is 1200 and closed is between true and false',
    condition: {
        logicOperator: 'AND',
        timingCondition: 'RECORD_HAS_TRANSITIONED_TO_THIS_STATUS',
        timingConditionConfig: {
            transitionedToStatus: {}
        },
        rules: [
            {
                context: 'NEW_VALUE',
                field: 'TOTPURCHASEPRICE',
                operator: 'IS_EQUAL_TO',
                values: ['1200']
            },
            {
                context: 'NEW_VALUE',
                field: 'BOOLCLOSED',
                operator: 'IS_BETWEEN',
                values: ['true', 'false']
            }
        ]
    },
    action: 'SEND_EMAIL',
    actionConfig: {
        sendMessage: {
            recipientType: 'USER',
            recipients: ['~~User~~'],
            subjectEnglish: 'AAA',
            subjectHebrew: 'AAA',
            bodyEnglish:
                "This email is to notify you that the value of 'Cost' is equal to 1200 and the 'Closed' status is between true and false. The current status of the record is '中國'.",
            bodyHebrew:
                "אימייל זה נועד להודיע לך כי הערך של 'עלות' שווה ל-1200 והסטטוס 'סגור' הוא בין נכון לשקרי. הסטטוס הנוכחי של הרשומה הוא '中國'."
        }
    }
};

export type BaseAIRulePayload = {
    query: string;
    columns: Array<{title: string; name: string; type: string}>;
};

type RecipientExtraData = {
    users: string[];
    groups: string[];
    webhooks: string[];
    recipientFields: Array<{title: string; name: string; type: string}>;
};

type AIRulePayload = BaseAIRulePayload &
    RecipientExtraData & {
        formName: string;
        fromStatus?: string;
        toStatus?: string;
    };

export const basicPOST = async <R>(path: string, params: unknown): Promise<R> =>
    fetch(`${getBaseURL()}/llm-services/${path}`, getPostRequestOptions(params))
        .then(res => {
            return res.json();
        })
        .then(data => data);

export const getAIBusinessRule = async (
    params: BaseAIRulePayload & RecipientExtraData & {formName: string}
) => {
    return basicPOST<AIAlertResponse>('generateBusinessRule', params);
};

export const getAutofillRule = async (params: BaseAIRulePayload & {formName: string}) => {
    return basicPOST<AIAlertResponse>('generateAutofillRule', params);
};

type BPMStatusRulePayload = BaseAIRulePayload &
    RecipientExtraData & {
        statusName: string; //  The status of the BPM node
        availableStatuses: string[]; // The list of available statuses from the BPM chart
    };

export const getBPMStatusRule = async (params: BPMStatusRulePayload) => {
    //
    return basicPOST<AIAlertResponse>('generateBpmRuleNode', params);
};

export const getBPMPathRule = async (
    params: BaseAIRulePayload & RecipientExtraData & {fromStatus: string; toStatus: string}
) => {
    return basicPOST<AIAlertResponse>('generateBpmRulePath', params);
};

export const getAISuggestedRule = async (
    params: AIRulePayload,
    type?: RuleType,
    isBPMPath?: boolean
): Promise<AIAlertResponse> => {
    const searchParams = new URLSearchParams(window.location.search);
    const returnTestResponse = searchParams.get('aiTest') === '1';

    if (returnTestResponse) {
        return new Promise(resolve => {
            setTimeout(() => {
                resolve(warnedRs);
            }, 1000);
        });
    }

    const basicParams = {
        columns: params.columns,
        query: params.query
    };

    const recipientExtraData = {
        users: params.users,
        groups: params.groups,
        webhooks: params.webhooks,
        recipientFields: params.recipientFields
    };

    switch (type) {
        case RuleType.autofill:
            return getAutofillRule({
                ...basicParams,
                formName: params.formName
            });
        case RuleType.bpm:
            if (isBPMPath) {
                return getBPMPathRule({
                    ...basicParams,
                    ...recipientExtraData,
                    fromStatus: params.fromStatus,
                    toStatus: params.toStatus
                });
            }
            return getBPMStatusRule({
                ...basicParams,
                ...recipientExtraData,
                statusName: params.toStatus,
                availableStatuses: getToStatuses(params.toStatus).map(({title}) => title)
            });
        default:
            return getAIBusinessRule({
                ...basicParams,
                ...recipientExtraData,
                formName: params.formName
            });
    }
};
